import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createEzyVetWebhook, deleteEzyVetWebhook, updateEzyVetSettings } from "@/api/integrations";
import { BASE_URL } from "@/api/client";
import { CopyField } from "@/components/ui/CopyField";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useToasts } from "@/hooks/useToast";
import { hmacSha256Hex } from "@/utils/hmac";
import type { ApiError, EzyVetStatus, EzyVetWebhookCreated } from "@/types";

/** The webhook URL ezyVet must call: the API's own origin when the server only knows the path. */
function absoluteUrl(url: string): string {
  return /^https?:\/\//.test(url) ? url : `${BASE_URL || window.location.origin}${url}`;
}

/** Webhook endpoint, one-time signing secret, a signature checker, and the opt-in for pushing alerts to ezyVet. */
export function WebhookEndpointCard({ status, isAdmin }: { status: EzyVetStatus; isAdmin: boolean }) {
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const [created, setCreated] = useState<EzyVetWebhookCreated | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["integrations", "ezyvet"] });
  const onError = (err: ApiError) => push(err.message ?? "Request failed", "error");

  const create = useMutation<EzyVetWebhookCreated, ApiError>({
    mutationFn: createEzyVetWebhook,
    onSuccess: (result) => {
      setCreated(result);
      refresh();
    },
    onError,
  });
  const remove = useMutation<void, ApiError>({
    mutationFn: deleteEzyVetWebhook,
    onSuccess: () => {
      setCreated(null);
      setConfirmOff(false);
      push("Webhooks turned off. VetGuard keeps checking ezyVet on a schedule.", "success");
      refresh();
    },
    onError,
  });
  const alerts = useMutation<EzyVetStatus, ApiError, boolean>({
    mutationFn: (on) => updateEzyVetSettings({ push_alerts: on }),
    onSuccess: (s) => {
      push(s.push_alerts ? "New critical flags will also appear as tasks in ezyVet." : "Alerts to ezyVet turned off.", "success");
      refresh();
    },
    onError,
  });
  const busy = create.isPending || remove.isPending || alerts.isPending;
  const header = created?.signature_header ?? status.webhook_signature_header ?? "X-Ezyvet-Signature";

  return (
    <section className="card" aria-labelledby="webhook-title">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 id="webhook-title" style={{ margin: 0 }}>
          Webhook endpoint
        </h2>
        <StatusBadge tone={status.webhook_url ? "synced" : "discrepancy"}>
          {status.webhook_url ? "Webhooks on" : `Polling every ${status.poll_minutes} min`}
        </StatusBadge>
      </div>
      <p className="muted" style={{ marginTop: 12 }}>
        {status.webhook_url
          ? `ezyVet notifies VetGuard of each consult and invoice change, so audits run within seconds. VetGuard still polls every ${status.poll_minutes} minutes in case a webhook is missed.`
          : `VetGuard checks ezyVet for changes every ${status.poll_minutes} minutes. Set up a webhook to have consults audited within seconds instead.`}
      </p>

      {created && (
        <div className="callout warning">
          <p style={{ marginTop: 0 }}>
            <b>Copy the signing secret now.</b> It won&apos;t be shown again. Give ezyVet support the URL, the secret and the
            header name, and ask them to send <span className="mono">consult</span> and{" "}
            <span className="mono">invoice</span> events.
          </p>
          <CopyField id="webhook-secret" label="Signing secret" value={created.signing_secret} secret />
        </div>
      )}

      {(created?.webhook_url ?? status.webhook_url) && (
        <>
          <CopyField id="webhook-url" label="Inbound webhook URL" value={absoluteUrl(created?.webhook_url ?? status.webhook_url!)} />
          <dl className="kv kv--compact">
            <dt>Signature header</dt>
            <dd className="mono">{header}</dd>
            <dt>Algorithm</dt>
            <dd>
              HMAC-SHA256 of the raw request body, hex encoded (<span className="mono">sha256=</span> prefix optional)
            </dd>
          </dl>
          <SignatureViewer header={header} url={absoluteUrl(created?.webhook_url ?? status.webhook_url!)} secret={created?.signing_secret} />
        </>
      )}

      {isAdmin && (
        <div className="row" style={{ flexWrap: "wrap", marginTop: 16 }}>
          <button className="btn btn-sm" disabled={busy} onClick={() => create.mutate()}>
            {status.webhook_url ? "Rotate URL and secret" : "Generate webhook endpoint"}
          </button>
          <Link className="btn btn-sm" to="/settings/integrations/ezyvet/logs">
            View webhook log
          </Link>
          {status.webhook_url && !confirmOff && (
            <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => setConfirmOff(true)}>
              Turn off webhooks
            </button>
          )}
          {confirmOff && (
            <>
              <span className="muted">The current URL will stop working.</span>
              <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => remove.mutate()}>
                Turn off
              </button>
              <button className="btn btn-sm" disabled={busy} onClick={() => setConfirmOff(false)}>
                Keep them on
              </button>
            </>
          )}
        </div>
      )}

      <hr className="divider" />
      <label className="toggle-row" style={{ cursor: isAdmin ? "pointer" : "default" }}>
        <input
          id="push-alerts"
          type="checkbox"
          checked={status.push_alerts}
          disabled={!isAdmin || busy}
          onChange={(e) => alerts.mutate(e.target.checked)}
        />
        <span>
          <b>Also raise new critical flags as tasks in ezyVet</b>
          <br />
          <span className="muted">
            Off by default, so VetGuard only reads from ezyVet. Needs write access to tasks in your API credentials. You can
            still push single flags from the reconciliation workbench.
          </span>
        </span>
      </label>
    </section>
  );
}

const SAMPLE_BODY = JSON.stringify({ id: "evt_test_001", event: "consult.created", data: { consult: { id: 501 } } });

/**
 * Computes the signature a webhook body should carry, so an admin can check
 * what ezyVet sends (or send a test with curl). Entirely in the browser.
 */
function SignatureViewer({ header, url, secret: initialSecret }: { header: string; url: string; secret?: string }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(initialSecret ?? "");
  const [body, setBody] = useState(SAMPLE_BODY);
  const [signature, setSignature] = useState<string | null>(null);

  useEffect(() => {
    if (initialSecret) setSecret(initialSecret);
  }, [initialSecret]);

  useEffect(() => {
    if (!open || !secret) {
      setSignature(null);
      return;
    }
    let cancelled = false;
    hmacSha256Hex(secret, body).then(
      (hex) => !cancelled && setSignature(hex),
      () => !cancelled && setSignature(null),
    );
    return () => {
      cancelled = true;
    };
  }, [open, secret, body]);

  const quoted = body.replace(/'/g, "'\\''");

  return (
    <div className="subsection">
      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {open ? "▾" : "▸"} Signature viewer
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <p className="muted">
            Paste the signing secret and a request body to see the header value VetGuard will accept. Nothing here is sent
            to a server.
          </p>
          <div className="field">
            <label htmlFor={`${id}-secret`}>Signing secret</label>
            <input
              id={`${id}-secret`}
              type="password"
              className="mono"
              autoComplete="off"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor={`${id}-body`}>Raw request body</label>
            <textarea id={`${id}-body`} style={{ minHeight: 80 }} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          {signature && (
            <>
              <CopyField id={`${id}-sig`} label="Expected header" value={`${header}: sha256=${signature}`} />
              <CopyField
                id={`${id}-curl`}
                label="Test delivery (curl)"
                value={`curl -X POST '${url}' -H 'Content-Type: application/json' -H '${header}: sha256=${signature}' --data-raw '${quoted}'`}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
